import { DELETE_FILE } from '@components/App/SystemDialogs/constants'
import { Checkbox, Loading, Tooltip } from '@lib/components'
import { IconTrash } from '@lib/icons'
import {
  FileIconBlank,
  FileIconExcel,
  FileIconPng,
  FileIconWord,
  FileIconZip,
} from '@lib/images'
import { timeAgo } from '@lib/utils/date'
import { formatBytes } from '@lib/utils/units'
import { StorageActions } from '@store/reducers/manager/storage'
import { openSystemDialog } from '@storeOld/reducers/ui'
import type React from 'react'
import { useState } from 'react'
import { useDispatch } from 'react-redux'

import { deleteProjectAttachment } from '@/services/project'
import type { AttachmentItem } from '@/types/models'
import css from './List.module.scss'

type Props = {
  file: AttachmentItem
  isSelected: boolean
  onClick: (file: AttachmentItem) => void
  selectedIds: Array<string>
  setSelectedIds: Function
}

interface IconProps {
  name: string
}

const FileIcon: React.FC<IconProps> = ({ name }) => {
  if (name.match(/\.jpe?g|png|gif|webp/)) {
    return <FileIconPng />
  }

  if (name.match(/\.zip|rar/)) {
    return <FileIconZip />
  }

  if (name.match(/\.xlsx?/)) {
    return <FileIconExcel />
  }

  if (name.match(/\.docx?/)) {
    return <FileIconWord />
  }

  return <FileIconBlank />
}

const Item: React.FC<Props> = ({
  file,
  isSelected,
  onClick,
  selectedIds,
  setSelectedIds,
}) => {
  const [loading, setLoading] = useState<boolean>(false)
  const dispatch = useDispatch()

  const handleDeleteFile = (file: AttachmentItem) => {
    dispatch(
      openSystemDialog(DELETE_FILE, {
        onDelete: async () => {
          setLoading(true)
          await deleteProjectAttachment(file.id)

          dispatch(StorageActions.removeAttachments([file]))
          setLoading(false)

          // Unselect deleted file
          const selectedIndex = selectedIds.findIndex((id) => file.id === id)
          if (selectedIndex !== -1) {
            setSelectedIds([
              ...selectedIds.slice(0, selectedIndex),
              ...selectedIds.slice(selectedIndex + 1),
            ])
          }
        },
      })
    )
  }

  return (
    <div className={css.item}>
      <div className={css.content}>
        <Checkbox
          name="item"
          size="large"
          checked={isSelected}
          onClick={() => onClick(file)}
        />

        <div className={css.icon}>
          <FileIcon name={file.filename} />
        </div>

        <div className={css.info}>
          <div className={css.title}>
            <a href={file.downloadUrl} target="_blank" rel="noopener">
              {file.filename.length > 18
                ? `${file.filename.slice(0, 18)}...`
                : file.filename}
            </a>
          </div>

          <div className={css.subInfo}>
            <div className={css.size}>{formatBytes(file.size)}</div>
            <div className={css.separator}>/</div>
            <div className={css.date}>{timeAgo(file.createdAt)}</div>
          </div>
        </div>

        <div>
          {loading ? (
            <div className={css.loading}>
              <Loading type="small" color="coral" variant="two" />
            </div>
          ) : (
            <div className={css.delete}>
              <Tooltip content="Delete" position="top">
                <IconTrash size={16} onClick={() => handleDeleteFile(file)} />
              </Tooltip>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Item
